import { ChangeDetectorRef, Injectable } from '@angular/core';
import { AbstractControl, UntypedFormGroup } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { AutoComplete } from 'primeng/autocomplete';
import { Dropdown } from 'primeng/dropdown';
import { RangeValidatorShared } from '../../models/interface/rangeVlidator.interface';
import { RequestAddressErrorKey } from '../../models/interface/requestAddressErrorKey.enum';

export type Validator = (id: string) => void;

@Injectable({
  providedIn: 'root',
})
export class ValidatorService {
  private dropDowns = new Map<string, Dropdown>();
  private autoCompleteList = new Map<string, AutoComplete>();
  commonErrors: string[] = [
    'invalid',
    'required',
    'min',
    'minlength',
    'max',
    'unavailable',
    'pattern',
    'maxlength',
    'expNotValidate',
    'minValue',
    'maxValue',
    'maxLength',
  ];
  customErrorsValue: string[] = ['minValue', 'maxValue'];

  constructor(private translate: TranslateService) {
    /** */
  }

  /**
   * register dropdown to fire validate on hide panel
   * @param id
   * @param dropDown
   */
  registerDropDown(id: string, dropDown: Dropdown): void {
    this.dropDowns[id] = dropDown;
  }

  /**
   * get dropdown prime module
   * @param id
   * @returns
   */
  getDropDown(id: string): Dropdown {
    return this.dropDowns[id];
  }

  /**
   * register autocomplete to fire validate on hide panel
   * @param {string} id
   * @param {AutoComplete} autoComplete
   */
  registerAutoComplete(id: string, autoComplete: AutoComplete): void {
    this.autoCompleteList[id] = autoComplete;
  }

  /**
   * get autoComplete prime module
   * @param {string} id
   * @returns {AutoComplete} response
   */
  getAutoComplete(id: string): AutoComplete {
    return this.autoCompleteList[id];
  }

  /**
   *  method to check if a formControl has any error.
   *  @param {string} id ID of formControl
   *  @return {} none, empty object or null if the formControl does not have any error
   */
  validateControl(formGroup: UntypedFormGroup, id: string, changeDetectorRef?: ChangeDetectorRef): void {
    changeDetectorRef?.detectChanges();
    const control = formGroup.get(id);
    const element = document.getElementById(id);
    let parentElement = element ? element.parentElement : null;
    let divError = parentElement ? parentElement.getElementsByClassName('error') : null;

    if (divError && divError.length === 0) {
      parentElement = element.closest('.mu-error-holder');
      divError = parentElement ? parentElement.getElementsByClassName('error') : null;
    }

    if (!control.errors) {
      if (!parentElement) {
        return;
      }

      parentElement.classList.remove('mu-field-error');
      parentElement.classList.add('mu-field-success');

      if (!divError || divError.length === 0) {
        return;
      }

      divError[0].innerHTML = '';
      return;
    }

    const errorData = this.getErrorsAndValues(control, id);
    parentElement?.classList.add('mu-field-error');
    parentElement?.classList.remove('mu-field-success');

    if (!divError || divError.length === 0) {
      return;
    }

    if (errorData.error !== RequestAddressErrorKey.FROM_API) {
      divError[0].innerHTML = this.translate.instant('form.error.' + errorData.error.split('Dynamic')[0], errorData.values);
    }
  }

  /**
   * get errors and values
   * @param {AbstractControl} control
   * @param {string} id
   * @returns {{[key: string]: any}} response
   */
  getErrorsAndValues(control: AbstractControl, id: string): { [key: string]: any } {
    let values: RangeValidatorShared = {};
    let error = Object.keys(control.errors)[0];
    const errorSpecificValues = Object.values(control.errors)[0];
    const errorWithValue = this.customErrorsValue.find(value => Object.keys(control.errors).includes(value));
    if (errorSpecificValues && Object.keys(errorSpecificValues).length > 0) {
      const errorValues = Object.values(errorSpecificValues);
      values = {
        current: errorValues[1],
        expected: errorValues[0],
      };
    }
    if (errorWithValue && control.errors[errorWithValue]) {
      values = {
        expectedValue: control.errors[errorWithValue],
      };
      error = errorWithValue + '.' + id;
    }
    if (!errorWithValue) {
      error = this.commonErrors.includes(error) ? error + '.' + id : error;
    }
    return { values, error };
  }

  /**
   * get validator by formGroup
   * @param formGroup
   * @param changeDetectorRef
   * @returns
   */
  getValidator(formGroup: UntypedFormGroup, changeDetectorRef?: ChangeDetectorRef): Validator {
    return (id: string) => {
      this.validateControl(formGroup, id, changeDetectorRef);
    };
  }

  /**
   * clear dropdowns
   */
  clear() {
    this.dropDowns.clear();
    this.autoCompleteList.clear();
  }

  /**
   * validate controls
   * @param formGroup
   * @param changeDetectorRef
   */
  validateControls(formGroup: UntypedFormGroup, changeDetectorRef: ChangeDetectorRef): void {
    changeDetectorRef.detectChanges();
    for (const id of Object.keys(formGroup.controls)) {
      if (id !== 'isInvalidForm') {
        this.validateControl(formGroup, id, changeDetectorRef);
      }
    }
  }

  /**
   * scroll to selected on show panel
   */
  async scrollToSelected(changeDetectorRef: ChangeDetectorRef, formData: UntypedFormGroup, field: string, optionList: any[]): Promise<void> {
    changeDetectorRef.detectChanges();
    const selected = document.getElementsByClassName('p-dropdown-item p-highlight p-ripple')[0] as HTMLElement;
    if (selected) {
      const element = document.getElementsByClassName('p-dropdown-items-wrapper')[0] as HTMLElement;
      const value = formData.get(field)?.value?.toString();
      const index = optionList.findIndex(x => x.value.toString() === value);
      element.scrollTop = index * selected.offsetHeight - 10;
    }
  }

  /**
   * scroll to selected points on show panel
   */
  async scrollToSelectedPoint(changeDetectorRef: ChangeDetectorRef, formData: UntypedFormGroup, field: string, optionList: any[]): Promise<void> {
    changeDetectorRef.detectChanges();
    const selected = document.getElementsByClassName('p-dropdown-item p-highlight p-ripple')[0] as HTMLElement;
    if (selected) {
      const element = document.getElementsByClassName('p-dropdown-items-wrapper')[0] as HTMLElement;
      const value = formData.get(field)?.value.toString();
      const index = optionList.findIndex(x => x.address.toString() === value);
      element.scrollTop = index * selected.offsetHeight - 10;
    }
  }
}
